// src/components/users/UserTable.tsx
// Table component for displaying users

import React from 'react';
import { Eye, CheckCircle, XCircle, Shield, Car, Users as UsersIcon } from 'lucide-react';
import { User } from '../../types/user';
import './UserTable.css';

interface UserTableProps {
  users: User[];
  loading: boolean;
  selectedUsers: string[];
  onToggleUser: (userId: string) => void;
  onToggleAll: () => void;
  onViewUser: (userId: string) => void;
}

const UserTable: React.FC<UserTableProps> = ({
  users,
  loading,
  selectedUsers,
  onToggleUser,
  onToggleAll,
  onViewUser,
}) => {
  const getRoleBadge = (role: string) => {
    const styles = {
      passenger: { bg: '#dbeafe', color: '#1e40af', icon: <UsersIcon size={14} /> },
      driver: { bg: '#fce7f3', color: '#be185d', icon: <Car size={14} /> },
      both: { bg: '#e0e7ff', color: '#4338ca', icon: <Shield size={14} /> },
    };

    const style = styles[role as keyof typeof styles] || styles.passenger;

    return (
      <span
        className="role-badge"
        style={{ background: style.bg, color: style.color }}
      >
        {style.icon}
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      active: { bg: '#d1fae5', color: '#065f46' },
      suspended: { bg: '#fed7aa', color: '#92400e' },
      banned: { bg: '#fecaca', color: '#991b1b' },
    };

    const style = styles[status as keyof typeof styles] || styles.active;

    return (
      <span
        className="status-badge"
        style={{ background: style.bg, color: style.color }}
      >
        {status}
      </span>
    );
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="table-loading">
        <div className="spinner"></div>
        <p>Loading users...</p>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="table-empty">
        <UsersIcon size={48} />
        <p>No users found</p>
      </div>
    );
  }

  return (
    <div className="table-container">
      <table className="users-table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={selectedUsers.length === users.length}
                onChange={onToggleAll}
              />
            </th>
            <th>User</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Role</th>
            <th>Status</th>
            <th>Verified</th>
            <th>Registered</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td>
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(user.id)}
                  onChange={() => onToggleUser(user.id)}
                />
              </td>
              <td>
                <div className="user-cell">
                  <div className="user-avatar">
                    {user.profileImage ? (
                      <img src={user.profileImage} alt={user.firstName} />
                    ) : (
                      <div className="avatar-placeholder">
                        {user.firstName[0]}
                        {user.lastName[0]}
                      </div>
                    )}
                  </div>
                  <div className="user-info">
                    <div className="user-name">
                      {user.firstName} {user.lastName}
                    </div>
                    {user.driverProfile && (
                      <div className="user-meta">
                        Driver: {user.driverProfile.approvalStatus}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td>{user.email}</td>
              <td>{user.phoneNumber || 'N/A'}</td>
              <td>{getRoleBadge(user.role)}</td>
              <td>{getStatusBadge(user.status)}</td>
              <td>
                {user.isVerified ? (
                  <CheckCircle size={20} className="icon-success" />
                ) : (
                  <XCircle size={20} className="icon-muted" />
                )}
              </td>
              <td>{formatDate(user.createdAt)}</td>
              <td>
                <button
                  onClick={() => onViewUser(user.id)}
                  className="btn-icon"
                  title="View Details"
                >
                  <Eye size={18} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UserTable;